import {
    Funcionario,
    FuncionarioCentroCusto,
    FuncionarioLocais,
} from '@/service/api'
import { useStore } from '@/store'
import dayjs from 'dayjs'

type Dependencies = {
    FuncionarioCentroCusto: FuncionarioCentroCusto[]
    FuncionarioLocais: FuncionarioLocais[]
    id: any
}

/**
 * Filtra os dados por centros de custo e locais
 * * Se centros de custo.length > 0, filtra conforme FuncionarioCentroCusto ativos
 * * Se locais.length > 0, filtra conforme FuncionarioLocais
 *
 * Os objetos devem ter as propriedades FuncionarioCentroCusto e FuncionarioLocais
 * @param locais Lista de IDs de locais para filtrar
 * @param centroCustoIds Lista de IDs de centros de custo para filtrar
 * @param accessor Função que retorna o objeto Funcionario a partir do item
 */
export function filterLocaisCentro<T>(
    locais: number[] = [],
    centroCustoIds: number[] = [],
    accessor: (item: T) => Dependencies = item => (item as any).Funcionario,
) {
    return (arrItem: T) => {
        // Usa a função accessor para obter o Funcionario
        const item = accessor(arrItem)

        if (!item) return false

        const centrosCustoValidos = centroCustoIds.filter(id => id > 0)

        // Verifica se o funcionário possui algum centro de custo ativo nos IDs fornecidos
        const hasCentroCusto =
            centrosCustoValidos.length > 0
                ? getActiveCentrosCustoFuncionario(item).some(cc =>
                      centrosCustoValidos.includes(Number(cc.centroCustoId)),
                  )
                : true

        const hasLocal =
            locais.length && Array.isArray(item.FuncionarioLocais)
                ? item.FuncionarioLocais.some(
                      (
                          local: Partial<
                              Funcionario['FuncionarioLocais'][number]
                          >,
                      ) => locais.includes(Number(local.localId)),
                  )
                : true

        return hasCentroCusto && hasLocal
    }
}

/**
 * Filtra os dados por centros de custo e locais,
 * usando os centros de custo armazenados no useStore.
 */
export function useFilterLocaisCentroStore<T = any>(
    accessor?: (item: T) => Dependencies,
) {
    const centroCustoIdsStore = useStore(state => state.costCenterId) // Supondo que agora seja um array
    const locaisStore = useStore(state => state.locaisIds)

    return filterLocaisCentro<T>(locaisStore, [centroCustoIdsStore], accessor)
}

/**
 * Obtém todos os centros de custo ativos do funcionário
 * Considera centros de custo sem dtFinal ou com dtFinal no futuro
 * @param Funcionario Objeto funcionário
 * @returns Lista de centros de custo ativos
 */
function getActiveCentrosCustoFuncionario({
    FuncionarioCentroCusto,
}: Partial<Funcionario>) {
    if (!FuncionarioCentroCusto || !FuncionarioCentroCusto.length) return []

    // Retorna todos os centros de custo ativos
    return FuncionarioCentroCusto.filter(
        cc => !cc.dtFinal || dayjs(cc.dtFinal).isAfter(dayjs(), 'day'),
    )
}
